import { useState, useEffect, type FormEvent } from 'react'
import { useAuth } from '../../hooks/useAuth.ts'
import { appPath } from '../../appConfig.ts'
import { listUsers, adminUpdateUser } from '../../api/auth.ts'
import { isApiError } from '../../api/client.ts'
import type { User } from '../../types/auth.ts'
import { CAPABILITY_GROUPS } from '../../utils/capabilities.ts'
import { RolesDialog } from './RolesDialog.tsx'

const OMS_ROLE_LABELS = new Map(
  CAPABILITY_GROUPS[0].items.map(i => [i.capability as string, i.label]),
)
const GIS_CAPS = new Set<string>(CAPABILITY_GROUPS[1].items.map(i => i.capability))

/** Role chips for the Users table: each outage role by name, GIS grants as a count. */
function RoleChips({ user }: { user: User }) {
  const caps = user.capabilities ?? []
  const omsLabels = caps.flatMap(c => OMS_ROLE_LABELS.get(c) ?? [])
  const gisCount = caps.filter(c => GIS_CAPS.has(c)).length
  return (
    <div className="flex flex-wrap gap-1">
      {user.is_admin && (
        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">Admin</span>
      )}
      {omsLabels.map(label => (
        <span key={label} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
          {label}
        </span>
      ))}
      {gisCount > 0 && (
        <span className="text-xs px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full">
          GIS ({gisCount})
        </span>
      )}
      {!user.is_admin && omsLabels.length === 0 && gisCount === 0 && (
        <span className="text-xs text-gray-400">User</span>
      )}
    </div>
  )
}

interface ResetState {
  userId: number
  username: string
  password: string
  confirm: string
  error: string | null
  loading: boolean
}

export function UserManagementPage() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [resetState, setResetState] = useState<ResetState | null>(null)
  const [rolesTarget, setRolesTarget] = useState<User | null>(null)

  function showToast(msg: string) {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(null), 3500)
  }

  useEffect(() => {
    if (!currentUser?.is_admin) return
    listUsers()
      .then(setUsers)
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load users'))
      .finally(() => setLoading(false))
  }, [currentUser])

  async function toggleActive(target: User) {
    try {
      const updated = await adminUpdateUser(target.id, { is_active: !target.is_active })
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u))
      showToast(`${updated.username} ${updated.is_active ? 'activated' : 'deactivated'}`)
    } catch (err) {
      showToast(isApiError(err) ? err.detail : 'Failed to update user')
    }
  }

  function rolesSaved(updated: User) {
    setUsers(prev => prev.map(u => u.id === updated.id ? updated : u))
    setRolesTarget(null)
    showToast(
      updated.id === currentUser?.id
        ? 'Your roles were updated — reload the page to see them'
        : `Roles updated for ${updated.username}`,
    )
  }

  function openReset(target: User) {
    setResetState({ userId: target.id, username: target.username, password: '', confirm: '', error: null, loading: false })
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault()
    if (!resetState) return
    if (resetState.password !== resetState.confirm) {
      setResetState(s => s ? { ...s, error: 'Passwords do not match' } : s)
      return
    }
    setResetState(s => s ? { ...s, loading: true, error: null } : s)
    try {
      await adminUpdateUser(resetState.userId, { new_password: resetState.password })
      showToast(`Password reset for ${resetState.username}`)
      setResetState(null)
    } catch (err) {
      setResetState(s => s ? { ...s, loading: false, error: isApiError(err) ? err.detail : 'Failed to reset password' } : s)
    }
  }

  if (!currentUser?.is_admin) {
    return (
      <div className="max-w-2xl mx-auto mt-12 px-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto mt-10 px-4">
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">Users</h2>
          <a
            href={appPath('/admin/add-user')}
            className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            + Add User
          </a>
        </div>

        {loading && (
          <div className="px-6 py-8 text-sm text-gray-500 text-center">Loading…</div>
        )}
        {error && (
          <div className="px-6 py-4 text-sm text-red-700 bg-red-50">{error}</div>
        )}

        {!loading && !error && (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3">
                    <div className="font-medium text-gray-900">{u.username}</div>
                    {u.full_name && <div className="text-xs text-gray-500">{u.full_name}</div>}
                  </td>
                  <td className="px-6 py-3 text-gray-600">{u.email}</td>
                  <td className="px-6 py-3">
                    <RoleChips user={u} />
                  </td>
                  <td className="px-6 py-3">
                    {u.is_active
                      ? <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Active</span>
                      : <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inactive</span>}
                  </td>
                  <td className="px-6 py-3 text-right space-x-3">
                    <button
                      onClick={() => setRolesTarget(u)}
                      className="text-xs text-purple-600 hover:text-purple-800 transition-colors"
                    >
                      Roles
                    </button>
                    {u.id !== currentUser.id && (
                      <>
                        <button
                          onClick={() => toggleActive(u)}
                          className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => openReset(u)}
                          className="text-xs text-amber-600 hover:text-amber-800 transition-colors"
                        >
                          Reset password
                        </button>
                      </>
                    )}
                    {u.id === currentUser.id && (
                      <span className="text-xs text-gray-300 italic">you</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Toast notification */}
      {toastMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-sm px-4 py-2 rounded-md shadow-lg">
          {toastMsg}
        </div>
      )}

      {rolesTarget && (
        <RolesDialog
          target={rolesTarget}
          onClose={() => setRolesTarget(null)}
          onSaved={rolesSaved}
        />
      )}

      {/* Reset-password modal */}
      {resetState && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-800 mb-1">Reset password</h3>
            <p className="text-sm text-gray-500 mb-4">
              Set a new password for <span className="font-medium text-gray-700">{resetState.username}</span>
            </p>

            <form onSubmit={submitReset} className="space-y-3">
              {resetState.error && (
                <div className="text-sm text-red-700 bg-red-50 p-2 rounded">{resetState.error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New password <span className="text-gray-400 font-normal">(min 8 chars)</span>
                </label>
                <input
                  type="password"
                  value={resetState.password}
                  onChange={e => setResetState(s => s ? { ...s, password: e.target.value } : s)}
                  required
                  autoFocus
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                <input
                  type="password"
                  value={resetState.confirm}
                  onChange={e => setResetState(s => s ? { ...s, confirm: e.target.value } : s)}
                  required
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={resetState.loading}
                  className="flex-1 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-50 transition-colors"
                >
                  {resetState.loading ? 'Saving…' : 'Set Password'}
                </button>
                <button
                  type="button"
                  onClick={() => setResetState(null)}
                  className="flex-1 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
